import { FolderIcon } from "@heroicons/react/24/solid";
import { Button, IconButton, Tooltip } from "@mui/material";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { LoadingState, ProjectHeader, FilterButton, DeleteModal, SideOverlay } from "../../components";
import { useGetFullProject } from "../../custom-hooks";
import { useStore, htmlValidation } from "../../helpers";
import { safe_get } from "../../report-visuals";
import { RNSearchBar } from "../influential-leaders";
import { AddFileOrFolder } from "./add-file-folder";
import { DAFilters } from "./da-filters";
import { FolderBreadCrumbs } from "./folder-bread-crumbs";
import { FileIcon, defaultStyles } from "react-file-icon";
import { FTPBreadCrumbs } from "../../components-new";
import ThumbnailImage from "../../components-new/Thumbnail";
import FullScreenImage from "../../components-new/FullscreenImage";
import { Delete, Edit, Visibility } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { PageShell } from "../../layouts/PageShell";
import ViewFile from "./view-file";

const DigitalRepository = observer((props: any) => {
    const { projectid } = useParams();
    const rootStore = useStore();
    const queryClient = useQueryClient();
    const { digitalArchiveStore, userStore } = rootStore;
    const { folderBreadCrumbs, activeParentId, daFilters, searchQuery } = digitalArchiveStore;
    const [s3Path, setS3Path] = useState({ path: "", type: "" });
    const [isModalOpen, setIsModalOpen] = useState(false); // For controlling the modal
    const [selectedFile, setSelectedFile] = useState(null); // To store the selected file for deletion
    const { t } = useTranslation("calendar");
    const { isLoading: fullProjectLoading } = useGetFullProject();

    useEffect(() => {
        if (folderBreadCrumbs.length > 0) {
            digitalArchiveStore.update_DAStoreProp(
                "activeParentId",
                folderBreadCrumbs[folderBreadCrumbs.length - 1]["id"]
            );
        }
    }, [folderBreadCrumbs]);

    useEffect(() => {
        return () => {
            digitalArchiveStore.resetFolderBreadCrumbs();
        };
    }, [projectid]);

    const crumbs = [
        { name: "DashBoard", href: `/project/${projectid}`, current: true },
        { name: "Digital Repository", href: ``, current: true },
    ];

    const getFiles = async () => {
        const payload = {
            parent_id: activeParentId,
            query: searchQuery,
            project_id: parseInt(projectid),
            filters: {
                date: daFilters.date,
            },
        };
        return await digitalArchiveStore.get_files_and_folders(payload);
    };

    const { data, isLoading, isError, refetch } = useQuery(
        ["files", activeParentId, projectid, searchQuery],
        getFiles
    );

    const { mutate } = useMutation({
        mutationFn: async (payload: any) => {
            const id = payload["id"];
            await digitalArchiveStore.delete_file_or_folder({ id });
        },
        onSuccess: async () => {
            setTimeout(async () => {
                await queryClient.invalidateQueries(["files", activeParentId, projectid]);
            }, 1000);
            toast.success("Deleted Successfully");
            setIsModalOpen(false);
        },
    });

    const deleteFile = async (e, file) => {
        htmlValidation(e);
        setSelectedFile(file);
        setIsModalOpen(true);
    };

    const confirmDelete = () => {
        if (selectedFile) {
            mutate(selectedFile);
        }
    };

    const cancelDelete = () => {
        setIsModalOpen(false);
    };

    const editFile = async (e, file) => {
        htmlValidation(e);
        digitalArchiveStore.update_DAStoreProp("addNewModal", true);
        digitalArchiveStore.update_DAStoreProp("activeFileorFolder", file);
    };

    const folderClickHandler = async (e, file) => {
        htmlValidation(e);
        const isFolder = safe_get(file, "is_folder", false);
        const mimetype = safe_get(file, "mimetype", null);

        if (!isFolder) {
            setS3Path({
                path: file.s3key,
                type: file.mimetype,
            });
            if (mimetype.includes("image")) {
                await userStore.update_thumbnail_modal(true);
            } else {
                window.open(safe_get(file, "s3_path", ""), "_blank");
            }

            return;
        }

        await digitalArchiveStore.update_DAStoreProp("activeParentId", file.id);

        const cc = _.clone(digitalArchiveStore.folderBreadCrumbs);
        const index = cc.findIndex((x) => x.id === file.id);
        if (index === -1) {
            cc.push(file);
        } else {
            cc.splice(index + 1);
        }

        await digitalArchiveStore.update_folder_breadcrumbs(cc);
    };

    const handleViewFile = (e: any, file: any) => {
        e.stopPropagation();
        htmlValidation(e);

        digitalArchiveStore.openViewFile(file);
    };

    if (isLoading || fullProjectLoading) return <LoadingState />;

    const sortedFilesAndFolders = data?.data.sort(function (x, y) {
        return y.is_folder - x.is_folder;
    });

    // Header actions component
    const headerActions = (
        <div className={"flex justify-end gap-x-2"}>
            <RNSearchBar onSearch={(val) => digitalArchiveStore.update_DAStoreProp("searchQuery", val)} />
            {searchQuery === "" && (
                <Button
                    startIcon={<i className={"fas fa-circle-plus"} />}
                    onClick={() => {
                        digitalArchiveStore.update_DAStoreProp("addNewModal", true);
                        digitalArchiveStore.update_DAStoreProp("activeFileorFolder", {});
                    }}
                    variant={"contained"}
                >
                    Add New
                </Button>
            )}
            <FilterButton
                filterOnClickHandler={() => digitalArchiveStore.update_DAStoreProp("da_filter_modal", true)}
            />
        </div>
    );

    return (
        <>
            {/* <ProjectHeader /> */}
            <PageShell
                title="Digital Archives"
                headerActions={headerActions}
                breadcrumbs={<FTPBreadCrumbs crumbs={crumbs} />}
                contentPadding="px-4"
            >
                <FolderBreadCrumbs
                    onFolderClick={(e, page) => {
                        htmlValidation(e);
                        if (activeParentId === page.parent) return;
                        folderClickHandler(e, page);
                    }}
                />
                {daFilters.date && (
                    <div className="my-3 flex items-center gap-2">
                        <span className="rounded-full bg-indigo-100 px-3 py-1 text-sm text-indigo-700">
                            Date: {daFilters.date}
                        </span>

                        <Button
                            size="small"
                            color="error"
                            onClick={async () => {
                                const filters = _.clone(digitalArchiveStore.daFilters);
                                filters.date = null;

                                await digitalArchiveStore.update_da_filters(filters);
                                await refetch();
                            }}
                        >
                            Clear Filter
                        </Button>
                    </div>
                )}

                <div
                    className={
                        "my-4 grid grid-cols-1 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
                    }
                >
                    {sortedFilesAndFolders.map((file) => {
                        console.log("file", file);
                        console.log(sortedFilesAndFolders);

                        const isFolder = safe_get(file, "is_folder", false);
                        const key = safe_get(file, "s3key", "").split("/");
                        const extension = key[key.length - 1].split(".")[1];
                        const file_type = !isFolder ? extension : "";

                        const mime = file.mimetype || "";
                        const isImage = mime.startsWith("image/");

                        // @ts-ignore

                        return (
                            <div
                                key={file.id}
                                className={"flex flex-row"}
                                onClick={(e) => {
                                    folderClickHandler(e, file);
                                }}
                                title={file.title}
                            >
                                <div
                                    className={
                                        "group relative flex w-full cursor-pointer flex-col items-center justify-center rounded-lg bg-white p-4 shadow-xl"
                                    }
                                >
                                    {/*<div*/}
                                    {/*  className={*/}
                                    {/*    "absolute top-[10px] right-3 flex flex-row items-center gap-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 text-common-black"*/}
                                    {/*  }*/}
                                    {/*>*/}
                                    {/*  <Tooltip title={t("tooltip.edit")}>*/}
                                    {/*    <IconButton*/}
                                    {/*      size="small"*/}
                                    {/*      onClick={(e) => editFile(e, file)}*/}
                                    {/*    >*/}
                                    {/*      <Edit fontSize="small" className="text-common-black" />*/}
                                    {/*    </IconButton>*/}
                                    {/*  </Tooltip>*/}
                                    {/*  <Tooltip title={t("tooltip.delete")}>*/}
                                    {/*    <IconButton*/}
                                    {/*      size="small"*/}
                                    {/*      onClick={(e) => deleteFile(e, file)}*/}
                                    {/*    >*/}
                                    {/*      <Delete fontSize="small" className="text-red-500" />*/}
                                    {/*    </IconButton>*/}
                                    {/*  </Tooltip>*/}
                                    {/*</div>*/}
                                    <div className="absolute right-3 top-[10px] z-10 flex items-center gap-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                        <Tooltip title="View">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewFile(e, file);
                                                }}
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title={t("tooltip.edit")}>
                                            <IconButton size="small" onClick={(e) => editFile(e, file)}>
                                                <Edit fontSize="small" className="text-common-black" />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title={t("tooltip.delete")}>
                                            <IconButton size="small" onClick={(e) => deleteFile(e, file)}>
                                                <Delete fontSize="small" className="text-red-500" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>

                                    {isFolder ? (
                                        <FolderIcon color={"#F8C471"} />
                                    ) : isImage ? (
                                        <ThumbnailImage
                                            key={file.id}
                                            s3Key={safe_get(file, "s3key", "")}
                                            bucket={"urn-assets"}
                                            width={250}
                                            height={196}
                                        />
                                    ) : (
                                        <FileIcon
                                            radius={1}
                                            fold={true}
                                            extension={file_type}
                                            {...defaultStyles[file_type]}
                                        />
                                    )}

                                    <span
                                        style={{
                                            maxWidth: "150px",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {file["title"]}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </PageShell>

            {isModalOpen && (
                <DeleteModal
                    isOpen={isModalOpen}
                    onDeleteClick={confirmDelete}
                    onCancelClick={cancelDelete}
                    id={selectedFile?.id}
                    desc={`Are you sure you want to delete?`}
                />
            )}

            <SideOverlay
                onClose={() => {
                    digitalArchiveStore.update_DAStoreProp("addNewModal", false);
                }}
                show={digitalArchiveStore.addNewModal}
                title={"Add New File/Folder"}
                children={<AddFileOrFolder />}
            />

            <SideOverlay
                onClose={() => {
                    digitalArchiveStore.update_DAStoreProp("da_filter_modal", false);
                }}
                show={digitalArchiveStore.da_filter_modal}
                title={"Digital Archive Filters"}
                children={<DAFilters onDateSave={async () => await refetch()} />}
            />
            <SideOverlay
                show={digitalArchiveStore.viewFileModal}
                title="View File"
                onClose={() => digitalArchiveStore.closeViewFile()}
            >
                <ViewFile />
            </SideOverlay>
            <FullScreenImage s3Key={s3Path.path} bucket={"urn-assets"} width={1080} height={700} />
        </>
    );
});

export default DigitalRepository;
